import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrdersAdmin = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/status`, { status: newStatus });
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success bg-green-50';
      case 'pending':
        return 'text-warning bg-yellow-50';
      case 'cancelled':
        return 'text-error bg-red-50';
      default:
        return 'text-text-muted bg-slate-50';
    }
  };

  if (loading) {
    return <div className="text-center py-12" data-testid="loading-indicator">Loading orders...</div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-heading font-semibold text-primary-main mb-6">Manage Orders</h2>

      {/* Orders Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background-subtle">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Order ID</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Customer</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Email</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Total</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Status</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Date</th>
                <th className="px-6 py-4 text-right text-sm font-heading font-semibold text-primary-main">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-text-muted">
                    No orders yet
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="border-t border-slate-100" data-testid={`order-row-${order.id}`}>
                    <td className="px-6 py-4 text-sm font-mono text-text-secondary">{order.id.substring(0, 8)}</td>
                    <td className="px-6 py-4 text-sm text-text-primary">{order.customer_name}</td>
                    <td className="px-6 py-4 text-sm text-text-secondary">{order.customer_email}</td>
                    <td className="px-6 py-4 text-sm font-mono text-text-primary">${order.total.toFixed(2)} AUD</td>
                    <td className="px-6 py-4">
                      <select
                        value={order.status}
                        onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                        className={`text-xs font-medium px-3 py-1 rounded-full border-0 outline-none cursor-pointer ${getStatusColor(
                          order.status
                        )}`}
                        data-testid={`order-status-${order.id}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-secondary">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="text-accent-main hover:text-accent-main/80 text-sm font-medium"
                        data-testid={`view-order-${order.id}`}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="order-details-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <h3 className="text-2xl font-heading font-bold text-primary-main mb-6">Order Details</h3>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-muted">Order ID:</span>
                    <p className="font-mono text-text-primary">{selectedOrder.id}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Status:</span>
                    <p className="capitalize text-text-primary">{selectedOrder.status}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Customer Name:</span>
                    <p className="text-text-primary">{selectedOrder.customer_name}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Email:</span>
                    <p className="text-text-primary">{selectedOrder.customer_email}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-text-muted">Shipping Address:</span>
                    <p className="text-text-primary">{selectedOrder.customer_address}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Order Date:</span>
                    <p className="text-text-primary">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Total:</span>
                    <p className="font-mono font-semibold text-text-primary">${selectedOrder.total.toFixed(2)} AUD</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-heading font-semibold text-primary-main mb-3">Items</h4>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-background-subtle">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary">Product</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-text-secondary">Quantity</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary">Price</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.items.map((item, idx) => (
                          <tr key={idx} className="border-t border-slate-100">
                            <td className="px-4 py-3 text-sm text-text-primary">{item.product_name}</td>
                            <td className="px-4 py-3 text-center text-sm text-text-secondary">{item.quantity}</td>
                            <td className="px-4 py-3 text-right text-sm font-mono text-text-secondary">
                              ${item.price.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-mono text-text-primary">
                              ${(item.price * item.quantity).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="btn-primary w-full mt-6"
                data-testid="close-order-details"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersAdmin;
